import { Info } from 'lucide-react';

import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { cn } from '@/lib/utils';

type Props = {
  label: string;
  hint?: string;
  // Pin the icon to the top for tall blocks (bordered lists, image upload,
  // textarea); plain inputs keep it centered.
  isTopAligned?: boolean;
  // Shrink the content to its natural width so the hint icon sits right next to it
  // (checkboxes, where a full-width field would push the icon far from the label).
  isInline?: boolean;
  children: ReactNode;
};

export const FieldWithHint = (props: Props) => {
  const {
    label,
    hint,
    isTopAligned = false,
    isInline = false,
    children,
  } = props;

  return (
    <div
      className={cn(
        'flex gap-1',
        isTopAligned ? 'items-start' : 'items-center',
      )}
    >
      <div className={cn('min-w-0', !isInline && 'flex-1')}>{children}</div>
      {hint ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground shrink-0"
              aria-label={label}
            >
              <Info className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-80 text-sm"
          >
            <p className="mb-1 font-medium">{label}</p>
            <p className="text-muted-foreground">{hint}</p>
          </PopoverContent>
        </Popover>
      ) : (
        // Matches the icon-sm button footprint (size-8).
        <div
          aria-hidden
          className="size-8 shrink-0"
        />
      )}
    </div>
  );
};
